import type {
  Acta,
  HistoricalComparisonData,
  MesaWithComparison,
} from "./types";
import { matchPartyByName } from "../../shared/lib/party-mappings";

const ACTAS_API_BASE = "https://actas.urbana.visualizando.ar/api";

/**
 * Fetch all actas from the actas API
 */
export async function fetchActas(): Promise<Acta[]> {
  try {
    const response = await fetch(`${ACTAS_API_BASE}/actas`);
    if (!response.ok) {
      throw new Error(`Failed to fetch actas: ${response.statusText}`);
    }
    return await response.json();
  } catch (error) {
    console.error("Error fetching actas:", error);
    throw error;
  }
}

/**
 * Calculate percentage difference between 2025 and 2023 results
 */
function calculatePercentageDiffs(
  acta: Acta,
  historicalData: HistoricalComparisonData,
): {
  percentageDiffs: Record<string, number>;
  avgDiff: number;
  partyComparison: {
    lla2025vsJxC_LLA2023: number;
    fuerzaPatria2025vs2023: number;
  } | null;
} {
  const percentageDiffs: Record<string, number> = {};

  if (!historicalData.general) {
    return { percentageDiffs, avgDiff: 0, partyComparison: null };
  }

  const historical2023 = historicalData.general.valoresTotalizadosPositivos;
  const totalVotes2025 = acta.data.totalVotes || acta.data.diputadosVotes;

  // Build a map of 2023 results by normalized party name
  const historical2023Map = new Map<string, number>();
  for (const party of historical2023) {
    const matched = matchPartyByName(party.nombreAgrupacion);
    if (matched) {
      historical2023Map.set(matched.key, party.votosPorcentaje);
    }
  }

  // Calculate percentage differences for each political force
  let totalDiff = 0;
  let count = 0;

  for (const force of acta.data.politicalForces) {
    // Match 2025 party by name to find corresponding 2023 data
    const matched2025 = matchPartyByName(force.name);
    if (!matched2025) continue;

    const percentage2023 = historical2023Map.get(matched2025.key);

    if (percentage2023 !== undefined && totalVotes2025 > 0) {
      const percentage2025 = (force.votes / totalVotes2025) * 100;
      const diff = percentage2025 - percentage2023;
      percentageDiffs[force.code] = diff;
      totalDiff += diff;
      count++;
    }
  }

  const avgDiff = count > 0 ? totalDiff / count : 0;

  // Calculate specific party comparisons using name-based matching
  let partyComparison: {
    lla2025vsJxC_LLA2023: number;
    fuerzaPatria2025vs2023: number;
  } | null = null;

  if (totalVotes2025 > 0) {
    // Find LLA 2025 by name
    const lla2025 = acta.data.politicalForces.find((f) => {
      const matched = matchPartyByName(f.name);
      return matched?.key === "lla";
    });

    // Find Fuerza Patria 2025 by name
    const fp2025 = acta.data.politicalForces.find((f) => {
      const matched = matchPartyByName(f.name);
      return matched?.key === "fp";
    });

    if (lla2025 || fp2025) {
      // Get 2023 percentages using party keys
      const jxc2023Pct = historical2023Map.get("jxc") || 0; // Juntos por el Cambio
      const lla2023Pct = historical2023Map.get("lla") || 0; // La Libertad Avanza
      const fp2023Pct = historical2023Map.get("fp") || 0; // Fuerza Patria

      const lla2025Pct = lla2025 ? (lla2025.votes / totalVotes2025) * 100 : 0;
      const fp2025Pct = fp2025 ? (fp2025.votes / totalVotes2025) * 100 : 0;

      partyComparison = {
        lla2025vsJxC_LLA2023: lla2025Pct - (jxc2023Pct + lla2023Pct),
        fuerzaPatria2025vs2023: fp2025Pct - fp2023Pct,
      };
    }
  }

  return { percentageDiffs, avgDiff, partyComparison };
}

/**
 * Fetch all actas with their historical comparisons
 */
export async function fetchActasWithComparisons(): Promise<
  MesaWithComparison[]
> {
  const actas = await fetchActas();

  // Filter to only include actas where showInDashboard is true (default is false)
  const filteredActas = actas.filter(
    (acta) => acta.data.showInDashboard === true,
  );

  // Use stored historical data (already fetched during acta ingestion)
  const actasWithComparisons = filteredActas.map((acta) => {
    // Use historicalData from the acta if available, otherwise use empty data
    const historicalData: HistoricalComparisonData = acta.data
      .historicalData || {
      general: null,
      runoff: null,
      error: "No historical data available",
    };

    const { percentageDiffs, avgDiff, partyComparison } =
      calculatePercentageDiffs(acta, historicalData);

    return {
      ...acta,
      percentageDiffs,
      avgDiff,
      partyComparison,
      historicalData,
    };
  });

  return actasWithComparisons;
}

/**
 * Calculate median of an array of numbers
 */
export function calculateMedian(values: number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    const val1 = sorted[mid - 1];
    const val2 = sorted[mid];
    if (val1 === undefined || val2 === undefined) return 0;
    return (val1 + val2) / 2;
  }

  const val = sorted[mid];
  return val ?? 0;
}

/**
 * Calculate average of an array of numbers
 */
export function calculateAverage(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((sum, val) => sum + val, 0) / values.length;
}
